import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Copy, GitBranch, Pencil, Plus, Search, Trash2, UserPlus } from 'lucide-react';
import { Dialog } from '../reusable';
import { useUIColors } from '../../hooks/useTheme';

const STATUS_NORMALIZATION_MAP = {
  Enviada: 'Emitida',
  Aprobada: 'Adjudicada',
};

const normalizeQuotationStatus = (status = '') =>
  STATUS_NORMALIZATION_MAP[String(status || '').trim()] || String(status || '').trim() || 'Borrador';

const QUOTATION_STATUS_STYLES = {
  Borrador: { bg: '#e5e7eb', text: '#374151', border: '#d1d5db' },
  Emitida: { bg: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' },
  Desierta: { bg: '#f3f4f6', text: '#4b5563', border: '#d1d5db' },
  Adjudicada: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
  Vendida: { bg: '#ccfbf1', text: '#0f766e', border: '#5eead4' },
  Rechazada: { bg: '#fce7f3', text: '#9d174d', border: '#f9a8d4' },
  Cancelada: { bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' },
  Perdida: { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
};

const formatDateEs = (iso) => {
  if (!iso) return '—';
  const raw = String(iso).trim();
  // YYYY-MM-DD (o prefijo de timestamp) como fecha local: evita el desfase UTC (−1 día en Chile).
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(raw);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatMoney = (value, currency = 'CLP') => {
  const n = Number(value || 0);
  const sym = currency === 'USD' ? 'US$' : currency === 'EUR' ? '€' : '$';
  return `${sym} ${n.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;
};

/** Total neto → total con IVA (19%). */
const grossAmountConIva = (netTotal) => {
  const t = Number(netTotal || 0);
  if (!Number.isFinite(t) || t === 0) return 0;
  return Math.round(t * 1.19 * 100) / 100;
};

const todayLocalYmd = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Misma regla que el backend: Qxxx → Vxxx. */
const suggestedSaleCodeFromQuotation = (row) => {
  const q = String(row?.quotation_code || '').trim();
  if (!q) return '';
  if (q.startsWith('Q')) return `V${q.slice(1)}`;
  return `V-${q}`;
};

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: 'Borrador', label: 'Borrador' },
  { value: 'Emitida', label: 'Emitida' },
  { value: 'Desierta', label: 'Desierta' },
  { value: 'Adjudicada', label: 'Adjudicada' },
  { value: 'Vendida', label: 'Vendida' },
  { value: 'Cancelada', label: 'Cancelada' },
  { value: 'Perdida', label: 'Perdida' },
  { value: 'Rechazada', label: 'Rechazada' },
];

/** Opciones para cambio masivo de estado (sin «Todos»). */
const BULK_STATUS_OPTIONS = STATUS_FILTER_OPTIONS.filter((o) => o.value !== '');

const TABLE_COLUMNS = [
  { key: 'Código', align: 'left', sortKey: 'quotation_code' },
  { key: 'Empresa', align: 'left', sortKey: 'organization_name' },
  { key: 'Contacto', align: 'left', sortKey: 'contact_name' },
  { key: 'Licitación', align: 'center', sortKey: 'tender' },
  { key: 'Ubicación', align: 'center', sortKey: 'location' },
  { key: 'Pago / entrega', align: 'center', sortKey: 'payment' },
  { key: 'Montos', align: 'right', sortKey: 'total_amount' },
  { key: 'Gal. T-COAT', align: 'right', sortKey: 'tcoat_gallons' },
  { key: 'Estado', align: 'center', sortKey: 'status' },
  { key: 'Cierre', align: 'left', sortKey: 'closing_date' },
];

/** Colores de canal (sin verde ni rojo). */
const CHANNEL_COLORS = {
  Directo: '#4f46e5',
  'M. Público - Compra Ágil': '#2563eb',
  'M. Público - Licitación': '#0284c7',
  'M. Público - Trato Directo': '#7c3aed',
  Senegocia: '#d97706',
  WhereX: '#8b5cf6',
};

const getChannelColor = (channel, fallbackMuted) => {
  const key = String(channel || '').trim();
  if (!key) return fallbackMuted;
  if (CHANNEL_COLORS[key]) return CHANNEL_COLORS[key];
  const lower = key.toLowerCase();
  const match = Object.keys(CHANNEL_COLORS).find((k) => k.toLowerCase() === lower);
  return match ? CHANNEL_COLORS[match] : '#6366f1';
};

const getSortValue = (row, sortKey) => {
  const commune = row.location_commune || '';
  const region = row.location_region || '';
  switch (sortKey) {
    case 'quotation_code':
      return String(row.quotation_code || '').toLowerCase();
    case 'organization_name':
      return String(row.organization_name || '').toLowerCase();
    case 'contact_name':
      return String(row.contact_name || '').toLowerCase();
    case 'tender':
      return String(row.tender || '').toLowerCase();
    case 'location':
      return `${commune} ${region}`.toLowerCase();
    case 'payment': {
      const pay = String(row.payment_terms || '').toLowerCase();
      const days = Number(row.delivery_days ?? 0);
      return `${pay} ${days}`;
    }
    case 'total_amount':
      return Number(row.total_amount) || 0;
    case 'tcoat_gallons':
      return Number(row.tcoat_gallons) || 0;
    case 'status':
      return normalizeQuotationStatus(row.status).toLowerCase();
    case 'closing_date':
      return String(row.closing_date || '');
    case 'sale_code':
      return String(row.sale_code || '').toLowerCase();
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey);
  const vb = getSortValue(b, sortKey);
  if (sortKey === 'closing_date') {
    const aEmpty = !String(va || '').trim();
    const bEmpty = !String(vb || '').trim();
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
  }
  if (typeof va === 'number' && typeof vb === 'number') {
    return (va - vb) * mult;
  }
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

/**
 * Tabla de cotizaciones (mismo look & feel que `Table3` de Leads): columnas apiladas según negocio.
 */
const formatGallons = (value) => {
  const n = Number(value || 0);
  if (!(n > 0)) return '—';
  return n.toLocaleString('es-CL', { maximumFractionDigits: 1 });
};

const QuotationsTable3 = ({
  rows = [],
  loading = false,
  error = '',
  isDarkMode = false,
  hideChrome = false,
  onRowOpen,
  onAdd,
  onBulkDelete,
  onBulkAssign,
  onDuplicate,
  onNewVersion,
  userFilterOptions = [],
  organizationFilterOptions = [],
  paymentTermsOptions = [],
  gallonsByQuotationId = null,
}) => {
  const ui = useUIColors();
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;

  const inputBg = ui.inputBg;
  const inputBorder = ui.inputBorder;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const [filterOrganizationId, setFilterOrganizationId] = useState('');
  const [sortColumn, setSortColumn] = useState('quotation_code');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkPending, setBulkPending] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignUserId, setAssignUserId] = useState('');
  const [assignStatus, setAssignStatus] = useState('');
  const [assignPaymentTerms, setAssignPaymentTerms] = useState('');
  const [assignClosingDate, setAssignClosingDate] = useState('');
  const [assignSaleCode, setAssignSaleCode] = useState('');
  const [assignLossReason, setAssignLossReason] = useState('');
  const headerSelectRef = useRef(null);

  const showBulkChrome = Boolean(onBulkDelete || onBulkAssign || onDuplicate || onNewVersion || onRowOpen);
  const colCount = showBulkChrome ? TABLE_COLUMNS.length + 1 : TABLE_COLUMNS.length;

  const rowsWithGallons = useMemo(() => {
    if (!gallonsByQuotationId) return rows;
    return (rows || []).map((row) => ({
      ...row,
      tcoat_gallons:
        row.tcoat_gallons != null
          ? Number(row.tcoat_gallons) || 0
          : gallonsByQuotationId.get(Number(row.id)) || 0,
    }));
  }, [rows, gallonsByQuotationId]);

  const filteredRows = useMemo(() => {
    let list = rowsWithGallons;

    const q = searchQuery.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((row) => {
        const hay = [
          row.quotation_code,
          row.quotation_date,
          row.organization_name,
          row.organization_unit_name,
          row.contact_name,
          row.tender,
          row.channel,
          row.location_commune,
          row.location_region,
          row.payment_terms,
          row.delivery_days,
          row.currency,
          row.total_amount,
          normalizeQuotationStatus(row.status),
          row.closing_date,
          row.sale_code,
          row.user_full_name,
          row.id,
          row.user_id,
          row.organization_id,
        ]
          .filter((x) => x !== undefined && x !== null)
          .map((x) => String(x))
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }

    if (filterStatus) {
      list = list.filter((row) => normalizeQuotationStatus(row.status) === filterStatus);
    }
    if (filterUserId !== '') {
      const uid = Number(filterUserId);
      list = list.filter((row) => Number(row.user_id || 0) === uid);
    }
    if (filterOrganizationId !== '') {
      const oid = Number(filterOrganizationId);
      list = list.filter((row) => Number(row.organization_id || 0) === oid);
    }

    return list;
  }, [rowsWithGallons, searchQuery, filterStatus, filterUserId, filterOrganizationId]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => compareRows(a, b, sortColumn, sortDir));
    return arr;
  }, [filteredRows, sortColumn, sortDir]);

  const visibleIds = useMemo(() => sortedRows.map((r) => Number(r.id)).filter((id) => Number.isFinite(id)), [sortedRows]);

  const allVisibleSelected =
    showBulkChrome && visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected = showBulkChrome && visibleIds.some((id) => selectedIds.includes(id));

  useEffect(() => {
    const el = headerSelectRef.current;
    if (!el || el.type !== 'checkbox') return;
    el.indeterminate = someVisibleSelected && !allVisibleSelected;
  }, [someVisibleSelected, allVisibleSelected, showBulkChrome]);

  useEffect(() => {
    if (selectedIds.length === 0) {
      setShowAssignModal(false);
      setAssignUserId('');
      setAssignStatus('');
      setAssignPaymentTerms('');
      setAssignClosingDate('');
      setAssignSaleCode('');
      setAssignLossReason('');
    }
  }, [selectedIds.length]);

  const toggleRowSelected = (rawId) => {
    const id = Number(rawId);
    if (!Number.isFinite(id)) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      if (visibleIds.length && visibleIds.every((id) => prev.includes(id))) {
        return prev.filter((id) => !visibleIds.includes(id));
      }
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const clearSelection = () => setSelectedIds([]);

  const handleSortClick = (key) => {
    if (sortColumn === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(key);
      setSortDir('asc');
    }
  };

  const selectedRows = useMemo(
    () => rows.filter((r) => selectedIds.includes(Number(r.id))),
    [rows, selectedIds]
  );

  const handleBulkDelete = async () => {
    if (!onBulkDelete || selectedIds.length === 0) return;
    const n = selectedIds.length;
    const ok = window.confirm(`¿Eliminar ${n} cotización${n === 1 ? '' : 'es'} seleccionada${n === 1 ? '' : 's'}?`);
    if (!ok) return;
    setBulkPending(true);
    try {
      await onBulkDelete(selectedIds);
      setSelectedIds([]);
    } catch (e) {
      window.alert(e?.message || 'No se pudo eliminar.');
    } finally {
      setBulkPending(false);
    }
  };

  const handleEditSelected = () => {
    if (!onRowOpen || selectedIds.length !== 1) return;
    const row = selectedRows[0];
    if (row) onRowOpen(row);
  };

  const openAssignModal = () => {
    if (!onBulkAssign || selectedIds.length === 0) return;
    const first = selectedRows[0];
    setAssignUserId('');
    setAssignStatus('');
    setAssignPaymentTerms('');
    setAssignClosingDate(todayLocalYmd());
    setAssignSaleCode(suggestedSaleCodeFromQuotation(first));
    setAssignLossReason('');
    setShowAssignModal(true);
  };

  const handleConfirmAssign = async () => {
    if (!onBulkAssign || selectedIds.length === 0) return;

    const payload = { quotation_ids: selectedIds };
    const uid = Number(assignUserId);
    if (Number.isFinite(uid) && uid > 0) payload.user_id = uid;
    if (assignStatus) payload.status = assignStatus;
    if (assignPaymentTerms !== '') payload.payment_terms = assignPaymentTerms;

    if (!payload.user_id && !payload.status && !('payment_terms' in payload)) {
      window.alert('Selecciona al menos un campo para asignar (ejecutivo, estado o forma de pago).');
      return;
    }

    if (assignStatus === 'Perdida') {
      const reason = String(assignLossReason || '').trim();
      if (!reason) {
        window.alert('Indica el motivo de pérdida.');
        return;
      }
      payload.loss_reason = reason;
    }

    setBulkPending(true);
    try {
      await onBulkAssign(payload);
      setShowAssignModal(false);
      setSelectedIds([]);
    } catch (e) {
      window.alert(e?.message || 'No se pudo asignar.');
    } finally {
      setBulkPending(false);
    }
  };

  const handleDuplicateSelected = async () => {
    if (!onDuplicate || selectedIds.length === 0) return;
    const n = selectedIds.length;
    const ok = window.confirm(
      `¿Duplicar ${n} cotización${n === 1 ? '' : 'es'}? Se creará${n === 1 ? '' : 'n'} con código nuevo y estado Borrador.`
    );
    if (!ok) return;
    setBulkPending(true);
    try {
      await onDuplicate(selectedIds);
      setSelectedIds([]);
    } catch (e) {
      window.alert(e?.message || 'No se pudo duplicar.');
    } finally {
      setBulkPending(false);
    }
  };

  const handleNewVersionSelected = async () => {
    if (!onNewVersion || selectedIds.length === 0) return;
    const n = selectedIds.length;
    const ok = window.confirm(
      `¿Crear nueva versión de ${n} cotización${n === 1 ? '' : 'es'}?\n` +
        `Se usará el mismo código con sufijo -XX y la versión anterior quedará Cancelada.`
    );
    if (!ok) return;
    setBulkPending(true);
    try {
      await onNewVersion(selectedIds);
      setSelectedIds([]);
    } catch (e) {
      window.alert(e?.message || 'No se pudo crear la nueva versión.');
    } finally {
      setBulkPending(false);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('');
    setFilterUserId('');
    setFilterOrganizationId('');
  };

  const hasActiveFilters =
    Boolean(searchQuery.trim()) ||
    Boolean(filterStatus) ||
    filterUserId !== '' ||
    filterOrganizationId !== '';
  const subtitle = loading
    ? 'Cargando…'
    : error ||
      (hasActiveFilters
        ? `Mostrando ${sortedRows.length} de ${rows.length} registros · A004_quotations`
        : `${rows.length} registros · A004_quotations`);

  const selectStyle = {
    height: '36px',
    padding: '0 10px',
    borderRadius: '8px',
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: '13px',
    minWidth: '200px',
    outline: 'none',
  };

  return (
    <div
      style={
        hideChrome
          ? { background: bg }
          : {
              border: `1px solid ${border}`,
              borderRadius: '12px',
              overflow: 'hidden',
              background: bg,
            }
      }
    >
      {hideChrome ? null : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${border}`,
            background: headerBg,
            flexWrap: 'wrap',
            gap: '10px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: text }}>Cotizaciones</h3>
            <p style={{ margin: 0, fontSize: '12px', color: muted }}>{subtitle}</p>
          </div>
          {onAdd ? (
            <button
              type="button"
              onClick={onAdd}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                height: '36px',
                padding: '0 14px',
                borderRadius: '8px',
                border: 'none',
                background: '#16a34a',
                color: '#fff',
                fontWeight: 600,
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              <Plus size={16} /> Nueva cotización
            </button>
          ) : null}
        </div>
      )}

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 220px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Buscar
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Código, cliente, comuna, estado, ejecutivo…"
              style={{ ...selectStyle, flex: 1, minWidth: 0 }}
            />
          </span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '160px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Estado
          </span>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={selectStyle}
          >
            {STATUS_FILTER_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        {userFilterOptions.length > 0 ? (
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '180px' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
              Ejecutivo
            </span>
            <select
              value={filterUserId}
              onChange={(e) => setFilterUserId(e.target.value)}
              style={selectStyle}
            >
              <option value="">Todos los ejecutivos</option>
              {userFilterOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {organizationFilterOptions.length > 0 ? (
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '200px' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
              Organización
            </span>
            <select
              value={filterOrganizationId}
              onChange={(e) => setFilterOrganizationId(e.target.value)}
              style={selectStyle}
            >
              <option value="">Todas las organizaciones</option>
              {organizationFilterOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              height: '36px',
              padding: '0 12px',
              borderRadius: '8px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: muted,
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              marginBottom: '2px',
            }}
          >
            Limpiar filtros
          </button>
        ) : null}
      </div>

      {showBulkChrome && selectedIds.length > 0 ? (
        <div
          style={{
            padding: '10px 16px',
            borderBottom: `1px solid ${border}`,
            background: isDarkMode ? '#1c1917' : '#f5f0e8',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          <div style={{ fontWeight: 700, fontSize: '13px', color: text, marginRight: '4px' }}>
            {selectedIds.length} seleccionada{selectedIds.length === 1 ? '' : 's'}
          </div>
          {onBulkDelete ? (
            <button
              type="button"
              disabled={bulkPending}
              onClick={handleBulkDelete}
              style={{
                height: '32px',
                padding: '0 12px',
                borderRadius: '6px',
                border: 'none',
                background: '#dc2626',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 700,
                cursor: bulkPending ? 'not-allowed' : 'pointer',
                opacity: bulkPending ? 0.7 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Trash2 size={14} aria-hidden />
              Eliminar
            </button>
          ) : null}
          {onRowOpen ? (
            <button
              type="button"
              disabled={bulkPending || selectedIds.length !== 1}
              onClick={handleEditSelected}
              title={selectedIds.length !== 1 ? 'Selecciona una sola fila para editar' : 'Editar'}
              style={{
                height: '32px',
                padding: '0 12px',
                borderRadius: '6px',
                border: 'none',
                background: selectedIds.length === 1 ? '#d97706' : '#9ca3af',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 700,
                cursor: bulkPending || selectedIds.length !== 1 ? 'not-allowed' : 'pointer',
                opacity: bulkPending ? 0.7 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Pencil size={14} aria-hidden />
              Editar
            </button>
          ) : null}
          {onBulkAssign ? (
            <button
              type="button"
              disabled={bulkPending}
              onClick={openAssignModal}
              style={{
                height: '32px',
                padding: '0 12px',
                borderRadius: '6px',
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 700,
                cursor: bulkPending ? 'not-allowed' : 'pointer',
                opacity: bulkPending ? 0.7 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <UserPlus size={14} aria-hidden />
              Asignar
            </button>
          ) : null}
          {onDuplicate ? (
            <button
              type="button"
              disabled={bulkPending}
              onClick={handleDuplicateSelected}
              style={{
                height: '32px',
                padding: '0 12px',
                borderRadius: '6px',
                border: 'none',
                background: '#0f766e',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 700,
                cursor: bulkPending ? 'not-allowed' : 'pointer',
                opacity: bulkPending ? 0.7 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Copy size={14} aria-hidden />
              Duplicar
            </button>
          ) : null}
          {onNewVersion ? (
            <button
              type="button"
              disabled={bulkPending}
              onClick={handleNewVersionSelected}
              style={{
                height: '32px',
                padding: '0 12px',
                borderRadius: '6px',
                border: 'none',
                background: '#7c3aed',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 700,
                cursor: bulkPending ? 'not-allowed' : 'pointer',
                opacity: bulkPending ? 0.7 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <GitBranch size={14} aria-hidden />
              Nueva versión
            </button>
          ) : null}
          <button
            type="button"
            disabled={bulkPending}
            onClick={clearSelection}
            style={{
              height: '32px',
              padding: '0 12px',
              borderRadius: '6px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: muted,
              fontSize: '12px',
              fontWeight: 600,
              cursor: bulkPending ? 'not-allowed' : 'pointer',
              marginLeft: 'auto',
            }}
          >
            Quitar selección
          </button>
        </div>
      ) : null}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: showBulkChrome ? '1280px' : '1240px' }}>
          <thead>
            <tr style={{ background: headerBg }}>
              {showBulkChrome ? (
                <th
                  scope="col"
                  style={{
                    width: '40px',
                    padding: `${thPadV}px 6px`,
                    borderBottom: `1px solid ${border}`,
                    verticalAlign: 'middle',
                  }}
                >
                  <input
                    ref={headerSelectRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    disabled={loading || sortedRows.length === 0 || bulkPending}
                    aria-label="Seleccionar todas las filas visibles"
                  />
                </th>
              ) : null}
              {TABLE_COLUMNS.map(({ key: h, align, sortKey: sk }) => (
                <th
                  key={h}
                  scope="col"
                  onClick={() => sk && handleSortClick(sk)}
                  onKeyDown={(e) => {
                    if (!sk) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSortClick(sk);
                    }
                  }}
                  tabIndex={sk ? 0 : undefined}
                  style={{
                    textAlign: align,
                    padding: `${thPadV}px ${padH + 4}px`,
                    fontSize: '11px',
                    fontWeight: 700,
                    color: muted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    borderBottom: `1px solid ${border}`,
                    cursor: sk ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
                      gap: '6px',
                      width: align === 'center' || align === 'right' ? '100%' : 'auto',
                    }}
                  >
                    {h}
                    {sk ? (
                      sortColumn === sk ? (
                        sortDir === 'asc' ? (
                          <ArrowUp size={13} strokeWidth={2.5} aria-hidden />
                        ) : (
                          <ArrowDown size={13} strokeWidth={2.5} aria-hidden />
                        )
                      ) : (
                        <ArrowUpDown size={13} strokeWidth={2} style={{ opacity: 0.4 }} aria-hidden />
                      )
                    ) : null}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                  Cargando cotizaciones…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                  {error || 'No hay cotizaciones para mostrar.'}
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                  Ningún resultado con la búsqueda actual.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const normalized = normalizeQuotationStatus(row.status);
                const palette = QUOTATION_STATUS_STYLES[normalized] || QUOTATION_STATUS_STYLES.Borrador;
                const rowId = Number(row.id);
                const rowSelected = showBulkChrome && selectedIds.includes(rowId);
                const currency = row.currency || 'CLP';
                const total = Number(row.total_amount || 0);
                const gross = grossAmountConIva(total);
                const initial = String(row.quotation_code || '?').trim().charAt(0).toUpperCase();

                return (
                  <tr
                    key={row.id}
                    style={{
                      borderBottom: `1px solid ${border}`,
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = rowHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    {showBulkChrome ? (
                      <td
                        style={{
                          padding: `${padV}px 6px`,
                          verticalAlign: 'middle',
                          width: '40px',
                          textAlign: 'center',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={rowSelected}
                          onChange={() => toggleRowSelected(row.id)}
                          disabled={bulkPending}
                          aria-label={`Seleccionar cotización ${row.quotation_code || row.id}`}
                        />
                      </td>
                    ) : null}
                    <td style={{ padding: `${padV}px 0`, verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', alignItems: 'stretch', minHeight: '36px' }}>
                        <div
                          style={{
                            width: '3px',
                            flexShrink: 0,
                            background: palette.border,
                            borderRadius: '0 2px 2px 0',
                          }}
                        />
                        <div style={{ display: 'flex', gap: '6px', padding: `2px ${padH + 2}px`, alignItems: 'center' }}>
                          <div
                            style={{
                              width: '26px',
                              height: '26px',
                              borderRadius: '7px',
                              background: isDarkMode ? '#27272a' : '#f0ebe3',
                              display: 'grid',
                              placeItems: 'center',
                              fontWeight: 800,
                              fontSize: '12px',
                              color: text,
                              flexShrink: 0,
                            }}
                          >
                            {initial}
                          </div>
                          <div>
                            <button
                              type="button"
                              onClick={() => onRowOpen?.(row)}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                margin: 0,
                                cursor: 'pointer',
                                textAlign: 'left',
                                fontWeight: 700,
                                fontSize: '12px',
                                color: '#2563eb',
                                lineHeight: 1.2,
                              }}
                            >
                              {row.quotation_code || '—'}
                            </button>
                            <div style={{ fontSize: '11px', color: muted, marginTop: '1px', lineHeight: 1.2 }}>
                              {formatDateEs(row.quotation_date)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '220px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: text, lineHeight: 1.2 }}>
                        {row.organization_name || '—'}
                      </div>
                      <div style={{ fontSize: '11px', fontWeight: 500, color: muted, marginTop: '2px', lineHeight: 1.2 }}>
                        {row.organization_unit_name?.trim() || '—'}
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '200px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: text, lineHeight: 1.2 }}>
                        {row.contact_name || '—'}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        verticalAlign: 'middle',
                        maxWidth: '160px',
                        textAlign: 'center',
                      }}
                    >
                      <div style={{ fontSize: '12px', fontWeight: 600, color: text, lineHeight: 1.2 }}>
                        {row.tender?.trim() || '—'}
                      </div>
                      <div
                        style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: getChannelColor(row.channel, muted),
                          marginTop: '2px',
                          lineHeight: 1.2,
                        }}
                      >
                        {row.channel?.trim() || '—'}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        verticalAlign: 'middle',
                        maxWidth: '180px',
                        textAlign: 'center',
                      }}
                    >
                      <div style={{ fontSize: '12px', color: text, lineHeight: 1.2 }}>{row.location_commune || '—'}</div>
                      <div style={{ fontSize: '11px', color: muted, marginTop: '2px', lineHeight: 1.2 }}>
                        {row.location_region || '—'}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        verticalAlign: 'middle',
                        maxWidth: '240px',
                        textAlign: 'center',
                      }}
                    >
                      <div style={{ fontSize: '12px', fontWeight: 600, color: text, lineHeight: 1.2 }}>
                        {row.payment_terms || '—'}
                      </div>
                      <div style={{ fontSize: '11px', color: muted, marginTop: '2px', lineHeight: 1.2 }}>
                        Entrega: {Number(row.delivery_days ?? 0).toLocaleString('es-CL')} días
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 700, fontSize: '13px', color: '#ea580c', lineHeight: 1.2 }}>
                        {formatMoney(gross, currency)}
                      </div>
                      <div style={{ fontSize: '11px', fontWeight: 500, color: muted, marginTop: '2px', lineHeight: 1.2 }}>
                        Sin IVA: {formatMoney(total, currency)}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        verticalAlign: 'middle',
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: '13px', color: text, lineHeight: 1.2 }}>
                        {formatGallons(row.tcoat_gallons)}
                      </div>
                      <div style={{ fontSize: '11px', color: muted, marginTop: '2px' }}>gal T-COAT</div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: '200px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            maxWidth: '100%',
                            padding: '2px 8px',
                            borderRadius: '999px',
                            fontSize: '10px',
                            fontWeight: 700,
                            letterSpacing: '0.02em',
                            background: palette.bg,
                            color: palette.text,
                            border: `1px solid ${palette.border}`,
                            lineHeight: 1.25,
                          }}
                        >
                          {normalized}
                        </span>
                        <span
                          style={{
                            fontSize: '11px',
                            color: row.user_full_name ? text : muted,
                            lineHeight: 1.25,
                            fontWeight: 500,
                            display: 'block',
                            maxWidth: '180px',
                            wordBreak: 'break-word',
                          }}
                        >
                          {row.user_full_name || '—'}
                        </span>
                      </div>
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        verticalAlign: 'middle',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <div style={{ fontSize: '12px', color: text, lineHeight: 1.2 }}>
                        {formatDateEs(row.closing_date)}
                      </div>
                      {normalized === 'Vendida' && String(row.sale_code || '').trim() ? (
                        <div
                          style={{
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#0d9488',
                            marginTop: '2px',
                            lineHeight: 1.2,
                          }}
                          title={`Pedido: ${row.sale_code}`}
                        >
                          {row.sale_code}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Dialog
        isOpen={showAssignModal}
        onClose={() => {
          if (!bulkPending) setShowAssignModal(false);
        }}
        title="Asignar a cotizaciones"
        subtitle={
          selectedIds.length === 1
            ? 'Asigna ejecutivo, estado o forma de pago a la cotización seleccionada. Deja en vacío lo que no quieras cambiar.'
            : `Asigna los mismos valores a ${selectedIds.length} cotizaciones. Deja en vacío lo que no quieras cambiar.`
        }
        size="md"
        isDarkMode={isDarkMode}
        zIndex={10050}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: '4px 0' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Ejecutivo</span>
            <select
              value={assignUserId}
              onChange={(e) => setAssignUserId(e.target.value)}
              disabled={bulkPending}
              style={{ ...selectStyle, height: '40px' }}
            >
              <option value="">Sin cambiar</option>
              {userFilterOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Estado</span>
            <select
              value={assignStatus}
              onChange={(e) => setAssignStatus(e.target.value)}
              disabled={bulkPending}
              style={{ ...selectStyle, height: '40px' }}
            >
              <option value="">Sin cambiar</option>
              {BULK_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Forma de pago</span>
            <select
              value={assignPaymentTerms}
              onChange={(e) => setAssignPaymentTerms(e.target.value)}
              disabled={bulkPending}
              style={{ ...selectStyle, height: '40px' }}
            >
              <option value="">Sin cambiar</option>
              {(paymentTermsOptions || [])
                .filter((o) => o.value)
                .map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
            </select>
          </label>

          {assignStatus === 'Perdida' ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Motivo de pérdida</span>
              <input
                type="text"
                value={assignLossReason}
                onChange={(e) => setAssignLossReason(e.target.value)}
                placeholder="Indica el motivo…"
                disabled={bulkPending}
                autoComplete="off"
                style={{ ...selectStyle, height: '40px', fontFamily: 'inherit' }}
              />
            </label>
          ) : null}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
            <button
              type="button"
              disabled={bulkPending}
              onClick={() => setShowAssignModal(false)}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: `1px solid ${border}`,
                background: inputBg,
                color: text,
                fontSize: '13px',
                fontWeight: 600,
                cursor: bulkPending ? 'not-allowed' : 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={bulkPending}
              onClick={handleConfirmAssign}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: bulkPending ? 'not-allowed' : 'pointer',
                opacity: bulkPending ? 0.75 : 1,
              }}
            >
              {bulkPending ? 'Guardando…' : 'Aplicar'}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default QuotationsTable3;
